import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrders, updateOrder } from '../api';
import {
  getMenu, updateMenuItem, removeMenuItem, uploadMenuPhoto, getStaffRestaurant,
  listCategories, saveCategories, clearStaffSession,
  staffGetPauseRules, staffSetPauseRules,
} from '../menuApi';
import AccountTab from '../components/kitchen/AccountTab';
import { css } from './kitchenPanelCss';
import { formatTimeInTz, getDateKeyInTz, convertToTimezone } from '../utils/timezone';
import { evaluatePauseState } from '../utils/pauseRules';
import RestaurantFooter from '../components/RestaurantFooter';
import { getHotels } from '../hotels';

/* ───────────────────────────────────────────────
   GapGo Kitchen — Liquid Glass KDS
   Tabs: Orders (live KDS) · Menu (per-hotel stock) · Settings
─────────────────────────────────────────────── */

const HOTELS = getHotels();

const APP_VERSION = 'v1.3.0';
const RESTAURANT_ID = '9669';

// `label` is what the chef taps. `code` is what the guest's email maps to
// approved wording with — backend/shared/notify.js REJECT_REASONS owns that
// copy, so kitchen shorthand never reaches a paying customer. Keep the codes in
// step with that file.
const REJECT_REASONS = [
  { code: 'item_unavailable', label: 'Item sold out' },
  { code: 'kitchen_closed', label: 'Kitchen closing' },
  { code: 'too_busy', label: 'Too busy right now' },
  { code: 'cannot_deliver', label: 'Cannot fulfil request' },
];

// ---- helpers ----
function getItems(order) {
  const raw = order.items || order.cart || [];
  return raw.map((it) => ({
    name: it.name || it.title || 'Item',
    qty: it.qty || it.quantity || 1,
    price: Number(it.price || 0),
    notes: it.notes || it.note || '',
  }));
}
function getTotal(order) {
  if (order.total != null) return Number(order.total);
  if (order.amount != null) return Number(order.amount);
  return getItems(order).reduce((s, it) => s + it.price * it.qty, 0);
}
function getCreated(order) {
  return order.createdAt || order.time || order.timestamp || order.created || null;
}
function getType(order) {
  const t = (order.orderType || order.type || (order.roomNumber ? 'room' : 'pickup') || '').toLowerCase();
  if (t.includes('room')) return 'room';
  if (t.includes('table')) return 'table';
  if (t.includes('deliver')) return 'delivery';
  return 'pickup';
}
function minutesSince(ts) {
  if (!ts) return 0;
  const then = new Date(ts).getTime();
  if (isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 60000));
}
function timeLabel(ts) {
  const m = minutesSince(ts);
  if (m < 1) return 'just now';
  if (m === 1) return '1 min';
  return m + ' min';
}

/**
 * Prep times a kitchen can commit to when accepting.
 *
 * Four options, not a picker: mid-service nobody scrolls a wheel. 20 is the
 * default because it matches the "20–30" every restaurant row currently carries
 * in `etaMins`, so the common case is a single tap.
 */
const PREP_OPTIONS = [10, 15, 20, 30];
const defaultPrepMins = 20;

/**
 * Minutes until the kitchen's promised ready time — negative once it is late.
 *
 * Falls back to counting UP from the order time for anything accepted before this
 * feature existed, so old orders still show something sensible rather than a
 * countdown from nowhere.
 */
function minutesUntil(ts) {
  if (!ts) return null;
  const then = new Date(ts).getTime();
  if (isNaN(then)) return null;
  return Math.round((then - Date.now()) / 60000);
}

/** "in 12 min" / "2 min over" — always says which side of the promise we are on. */
function countdownLabel(readyBy) {
  const m = minutesUntil(readyBy);
  if (m === null) return null;
  if (m > 1) return `in ${m} min`;
  if (m === 1) return 'in 1 min';
  if (m === 0) return 'due now';
  return `${Math.abs(m)} min over`;
}
const TYPE_META = {
  room: { label: 'Room', color: '#2563EB' },
  table: { label: 'Table', color: '#60A5FA' },
  pickup: { label: 'Pickup', color: '#94A3B8' },
  delivery: { label: 'Delivery', color: '#BFDBFE' },
};

const PRIORITY_META = {
  VIP:    { label: 'VIP',  cls: 'kp-pri-vip' },
  HIGH:   { label: 'HIGH', cls: 'kp-pri-high' },
  MEDIUM: { label: null, cls: null },
  LOW:    { label: null, cls: null },
};

function fireNotification(newOrders) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const count = newOrders.length;
  const title = count === 1 ? 'New Order' : `${count} New Orders`;
  const lines = newOrders.slice(0, 3).map((o) => {
    const hotel = getHotelLabel(o.hotelId) || 'Kitchen';
    const items = getItems(o).slice(0, 2).map((it) => `${it.qty}× ${it.name}`).join(', ');
    return `${hotel}: ${items}`;
  });
  if (newOrders.length > 3) lines.push(`+${newOrders.length - 3} more`);
  try {
    new Notification(title, { body: lines.join('\n'), icon: '/favicon.ico', tag: 'gapgo-new-order', requireInteraction: false });
  } catch (e) { /* some browsers block in certain contexts */ }
}

function getHotelLabel(hotelId) {
  if (!hotelId) return null;
  const h = HOTELS.find((h) => h.id === hotelId);
  return h ? h.name : hotelId;
}

const TIMEZONES = [
  { id: 'AEST', name: 'AEST (Australian Eastern) UTC+10' },
  { id: 'AEDT', name: 'AEDT (Australian Eastern Summer) UTC+11' },
  { id: 'ACST', name: 'ACST (Australian Central) UTC+9:30' },
  { id: 'ACDT', name: 'ACDT (Australian Central Summer) UTC+10:30' },
  { id: 'AWST', name: 'AWST (Australian Western) UTC+8' },
  { id: 'UTC', name: 'UTC (Coordinated Universal Time) UTC+0' },
];

const DEFAULT_SETTINGS = {
  sound: true, volume: 0.7, autoRefresh: true,
  refreshSecs: 20, textSize: 'normal', lateAfter: 15,
  timezone: 'AEST',
  // 'focus' = one stage at a time, big rows, built for a tablet.
  // 'classic' = the original three columns, kept for one release so a kitchen
  // that hates the change is one toggle from the screen it knows.
  kitchenView: 'focus',
};

function KitchenPanel() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');
  const [hotel, setHotel] = useState(HOTELS[0]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  // filters
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  // live wall clock
  const [clock, setClock] = useState(new Date());

  // profile panel
  const [profileOpen, setProfileOpen] = useState(false);

  // history filters
  const [histDate, setHistDate] = useState('today');
  const [histStatus, setHistStatus] = useState('all');
  const [histHotel, setHistHotel] = useState('all');
  const [histQuery, setHistQuery] = useState('');

  // settings — persisted across sessions
  const [settings, setSettings] = useState(() => {
    try {
      const s = localStorage.getItem('gapgo_kitchen_settings');
      return s ? { ...DEFAULT_SETTINGS, ...JSON.parse(s) } : DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  });
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Which restaurant this screen belongs to. Display only — which ORDERS it can
  // see is derived server-side from the signed token, never from this.
  //
  // Memoised because getStaffRestaurant() builds a fresh object from localStorage on
  // every call: as a plain const it would be a new identity each render, and the
  // effects that read it would re-run on every one of the 1s clock ticks.
  const staffRestaurant = useMemo(() => getStaffRestaurant(), []);

  // pause mode — restaurant closed but limited items still available
  const [pauseMode, setPauseMode] = useState(() => {
    try {
      const p = localStorage.getItem('gapgo_pause_mode_' + HOTELS[0].id);
      return p ? JSON.parse(p) : { active: false, availableCategories: ['Beverages', 'Snacks'] };
    } catch { return { active: false, availableCategories: ['Beverages', 'Snacks'] }; }
  });

  // calculated pause state from rules engine (or manual override)
  const [pauseState, setPauseState] = useState({ paused: false, availableCategories: [], reason: null, ruleId: null });

  useEffect(() => {
    try { localStorage.setItem('gapgo_kitchen_settings', JSON.stringify(settings)); } catch {}
  }, [settings]);

  // menu
  const [menu, setMenu] = useState([]);
  const [menuLoaded, setMenuLoaded] = useState(false);
  const [menuQuery, setMenuQuery] = useState('');
  const [savingItem, setSavingItem] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [menuEditorOpen, setMenuEditorOpen] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [menuDraft, setMenuDraft] = useState(null);
  // Deleting a menu item used to happen on a single tap inside the editor with no
  // confirmation — on a tablet, in a kitchen. This holds the item awaiting a yes.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [menuStockFilter, setMenuStockFilter] = useState('all');

  // Managed categories. Previously a free-text field per item plus a hardcoded order
  // array, so "Deserts" silently became its own section on the guest menu.
  const [categories, setCategories] = useState([]);
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [catDraft, setCatDraft] = useState([]);
  const [catSaving, setCatSaving] = useState(false);

  // Account tab
  const [acctSection, setAcctSection] = useState('profile');

  // Service hours, as the SERVER holds them. These belong to the restaurant, not to
  // a hotel — and they used to live only in this tablet's localStorage, which is why
  // guests never saw them. Held in state so the settings sheet renders from one
  // place instead of re-reading storage three times mid-render.
  const [pauseRules, setPauseRulesState] = useState({ recurringRules: [], manualPause: { active: false, until: null } });

  // reject flow
  const [rejecting, setRejecting] = useState(null);

  // The order currently demanding a decision, shown full-screen. A card in a
  // column plus a sound was missable in a busy kitchen — which is how an A$89
  // order sat untouched on `new` for a day. This cannot be walked past.
  const [incoming, setIncoming] = useState(null);
  // An order opened for a closer look (items, notes, guest, timeline).
  const [detail, setDetail] = useState(null);
  // Which stage the focus view is showing. Starts on New — the only one that is
  // ever urgent.
  const [stage, setStage] = useState('new');

  // rule editor
  const [ruleEditorOpen, setRuleEditorOpen] = useState(false);
  const [ruleDraft, setRuleDraft] = useState(null);

  // toast
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }, []);

  const audioCtx = useRef(null);
  const prevNewIds = useRef(new Set());
  const isFirstLoad = useRef(true);
  const alarmTimer = useRef(null);
  const alarmRunning = useRef(false);
  const pendingNewCount = useRef(0);

  // ---- tick clock for live timers ----
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(t);
  }, []);

  // ---- 1-second wall clock ----
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ---- request browser notification permission ----
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // ---- load pause mode for current hotel ----
  useEffect(() => {
    try {
      const p = localStorage.getItem('gapgo_pause_mode_' + hotel.id);
      setPauseMode(p ? JSON.parse(p) : { active: false, availableCategories: ['Beverages', 'Snacks'] });
    } catch { /* keep current */ }
  }, [hotel.id]);

  // ---- evaluate pause state from rules engine ----
  useEffect(() => {
    const evaluate = () => {
      const state = evaluatePauseState(pauseRules, settings.timezone);
      setPauseState(state);
    };
    evaluate();
    // Re-evaluate every minute so a rule that starts at 22:00 takes effect without
    // anyone reloading the tablet.
    const timer = setInterval(evaluate, 60000);
    return () => clearInterval(timer);
    // Rules are the restaurant's, so `hotel.id` is deliberately NOT a dependency —
    // switching the venue selector must not re-evaluate different opening hours.
  }, [pauseRules, settings.timezone]);

  // ---- load orders ----
  const loadOrders = useCallback(async () => {
    try {
      const data = await getOrders();
      const list = Array.isArray(data) ? data : (data && data.orders) || [];
      setOrders(list);

      // detect genuinely new orders (skip on initial load to avoid false alerts)
      const activeNew = list.filter((o) => (o.status || 'new') === 'new').map((o) => o.orderId || o.id);
      const genuinelyNew = isFirstLoad.current ? [] : list.filter((o) => (o.status || 'new') === 'new' && !prevNewIds.current.has(o.orderId || o.id));
      const hasIncoming = genuinelyNew.length > 0;
      isFirstLoad.current = false;
      prevNewIds.current = new Set(activeNew);
      if (hasIncoming && settings.sound) playAlert();
      if (hasIncoming) fireNotification(genuinelyNew);

      // Put the oldest un-actioned order in front of the kitchen. A card in a
      // column plus a sound was missable — an A$89.20 order sat on `new` for a
      // whole day. Oldest first, because the guest who has waited longest is the
      // one most at risk of never getting food.
      if (hasIncoming) {
        setIncoming((cur) => cur || genuinelyNew.sort(
          (a, b) => new Date(getCreated(a)) - new Date(getCreated(b))
        )[0]);
      }
    } catch (e) {
      // keep last good state
    } finally {
      setLoading(false);
    }
    // `playAlert` is deliberately omitted. It is recreated whenever the volume
    // setting changes, and including it here would rebuild this callback — and the
    // polling interval that depends on it — mid-alert, cutting the alarm off while
    // an order is still waiting to be acknowledged. The alarm is the one thing on
    // this screen that must not be interruptible by a settings change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.sound]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!settings.autoRefresh) return;
    const t = setInterval(loadOrders, Math.max(5, settings.refreshSecs) * 1000);
    return () => clearInterval(t);
  }, [settings.autoRefresh, settings.refreshSecs, loadOrders]);

  // ---- load menu + categories ----
  useEffect(() => {
    let active = true;
    setMenuLoaded(false);
    getMenu(hotel.id, staffRestaurant && staffRestaurant.restaurantId).then((items) => {
      if (active) {
        setMenu(items);
        setMenuLoaded(true);
      }
    });
    // Categories are best-effort: the endpoint is new, so a kitchen running against
    // an older Lambda must still get a working menu screen. listCategories falls back
    // to deriving them from the items it can see.
    listCategories(hotel.id)
      .then((cats) => { if (active) setCategories(cats); })
      .catch(() => { if (active) setCategories([]); });
    staffGetPauseRules()
      .then((p) => { if (active && p) setPauseRulesState(p); })
      .catch(() => { /* older Lambda, or offline — keep what we have */ });
    return () => { active = false; };
  }, [hotel, staffRestaurant]);

  // ---- sound ----
  const playAlert = useCallback(() => {
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      // Compressor pushes everything to perceived maximum — kitchen-grade loudness
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -6;
      comp.knee.value = 3;
      comp.ratio.value = 20;
      comp.attack.value = 0.001;
      comp.release.value = 0.05;
      comp.connect(ctx.destination);

      const vol = settings.volume;
      // Four sharp blasts with layered sawtooth + sub-bass kick for carry across noise
      const blasts = [
        { d: 0.00, f: 480, f2: 960 },
        { d: 0.22, f: 540, f2: 1080 },
        { d: 0.44, f: 420, f2: 840 },
        { d: 0.66, f: 600, f2: 1200 },
      ];
      blasts.forEach(({ d, f, f2 }) => {
        const t0 = ctx.currentTime + d;
        // Layer 1: main sawtooth (carries through noise)
        const o1 = ctx.createOscillator(); const g1 = ctx.createGain();
        o1.type = 'sawtooth';
        o1.frequency.setValueAtTime(f, t0);
        o1.frequency.linearRampToValueAtTime(f * 1.35, t0 + 0.12);
        g1.gain.value = 0.0001;
        g1.gain.exponentialRampToValueAtTime(vol, t0 + 0.008);
        g1.gain.exponentialRampToValueAtTime(vol * 0.6, t0 + 0.09);
        g1.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
        o1.connect(g1); g1.connect(comp);
        o1.start(t0); o1.stop(t0 + 0.20);
        // Layer 2: octave up sawtooth (brightness)
        const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
        o2.type = 'sawtooth';
        o2.frequency.setValueAtTime(f2, t0);
        o2.frequency.linearRampToValueAtTime(f2 * 1.2, t0 + 0.10);
        g2.gain.value = 0.0001;
        g2.gain.exponentialRampToValueAtTime(vol * 0.75, t0 + 0.008);
        g2.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.15);
        o2.connect(g2); g2.connect(comp);
        o2.start(t0); o2.stop(t0 + 0.18);
        // Layer 3: sub-bass thump (felt, not just heard)
        const o3 = ctx.createOscillator(); const g3 = ctx.createGain();
        o3.type = 'sine';
        o3.frequency.setValueAtTime(f * 0.25, t0);
        o3.frequency.linearRampToValueAtTime(f * 0.1, t0 + 0.08);
        g3.gain.value = 0.0001;
        g3.gain.exponentialRampToValueAtTime(vol * 0.9, t0 + 0.005);
        g3.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
        o3.connect(g3); g3.connect(comp);
        o3.start(t0); o3.stop(t0 + 0.14);
      });
    } catch (e) { /* ignore */ }
  }, [settings.volume]);

  const stopAlarm = useCallback(() => {
    alarmRunning.current = false;
    if (alarmTimer.current) { clearTimeout(alarmTimer.current); alarmTimer.current = null; }
  }, []);

  const startAlarm = useCallback(() => {
    if (!settings.sound || pendingNewCount.current <= 0 || alarmRunning.current) return;
    alarmRunning.current = true;
    const tick = () => {
      if (!alarmRunning.current || pendingNewCount.current <= 0) { stopAlarm(); return; }
      playAlert();
      alarmTimer.current = setTimeout(tick, 2600);
    };
    tick();
  }, [settings.sound, playAlert, stopAlarm]);

  // ---- order actions ----
  async function setStatus(order, status, extra) {
    const id = order.orderId || order.id;
    // optimistic update — never roll back; next poll will re-sync if needed
    setOrders((prev) => prev.map((o) => ((o.orderId || o.id) === id ? { ...o, status, ...(extra || {}) } : o)));
    try {
      await updateOrder(id, { status, ...(extra || {}) });
    } catch (e) {
      console.error('updateOrder:', e);
      // keep the optimistic state — chef's action stands; background poll will correct if needed
    }
  }

  /**
   * Accepting an order COMMITS a prep time.
   *
   * This is the difference between a ticket screen and a merchant app. Uber Eats
   * and Menulog make the kitchen say how long it will take, and everything after
   * counts down against that promise rather than counting up from when the guest
   * ordered — which is a number the kitchen can do nothing about.
   *
   * `prepMins` and `readyBy` ride the existing patch API: `updateOrder(id, patch)`
   * already forwards arbitrary fields and the Lambda now allows these two. Nothing
   * else in the order path changes, and no guest email fires — see the note in
   * backend/functions/updateOrder/index.js for why that is deliberate.
   */
  function accept(order, prepMins) {
    const mins = Number(prepMins) || defaultPrepMins;
    const readyBy = new Date(Date.now() + mins * 60000).toISOString();
    setStatus(order, 'preparing', { prepMins: mins, readyBy });
    setIncoming(null);
    showToast(`Accepted — ready in ${mins} min`);
  }
  function markReady(order) { setStatus(order, 'ready'); }
  function complete(order) { setStatus(order, 'completed'); showToast('Order completed'); }
  function bumpBack(order) { setStatus(order, 'preparing'); }
  function confirmReject(order, reason) {
    // rejectReason stays free text for internal history; rejectReasonCode is what
    // the guest's email turns into approved wording.
    setStatus(order, 'rejected', { rejectReason: reason.label, rejectReasonCode: reason.code });
    setRejecting(null);
    showToast('Order rejected: ' + reason.label);
  }
  // Parked, deliberately: no code anywhere calls stripe.refunds.create yet, so this
  // marks the order and tells the truth about it. Kept wired so the button is a
  // one-line change the day the refund service lands, rather than a rewrite.
  // eslint-disable-next-line no-unused-vars
  function refund(order) {
    setStatus(order, 'refunded', { refundRequested: true });
    showToast('Marked for refund. Stripe refund runs once the refund service is live.');
  }
  function recallLast() {
    const done = orders
      .filter((o) => (o.status || '') === 'completed')
      .sort((a, b) => new Date(getCreated(b)) - new Date(getCreated(a)));
    if (!done.length) { showToast('No completed orders to recall'); return; }
    setStatus(done[0], 'ready');
    showToast('Recalled to Ready');
  }

  // quick 86 from an order item
  async function eightySix(itemName) {
    const match = menu.find((m) => m.name.toLowerCase() === itemName.toLowerCase());
    if (!match) { showToast('"' + itemName + '" is not in this menu'); return; }
    await toggleStock(match, false);
    showToast('"' + itemName + '" marked out of stock');
  }

  // ---- menu actions ----
  //
  // Every write here used to discard its result. `updateMenuItem` and
  // `removeMenuItem` return `{persisted:false}` when the server call fails and the
  // change only landed in localStorage — so a price edit or a delete looked like it
  // worked, and then reappeared on the next refresh when the remote menu was merged
  // back over the top. That is why the live menu still carries test items nobody
  // could get rid of. Failures are surfaced now.
  async function toggleStock(item, value) {
    const next = value != null ? value : !item.inStock;
    const before = item.inStock;
    setMenu((prev) => prev.map((m) => (m.itemId === item.itemId ? { ...m, inStock: next } : m)));
    setSavingItem(item.itemId);
    const res = await updateMenuItem(hotel.id, { ...item, inStock: next });
    setSavingItem(null);
    if (res && res.persisted === false) {
      // Roll the row back rather than leaving the screen claiming something the
      // guest menu does not agree with. An item shown in stock that is actually 86'd
      // is an order the kitchen cannot fill.
      setMenu((prev) => prev.map((m) => (m.itemId === item.itemId ? { ...m, inStock: before } : m)));
      showToast('Could not save — check your connection and try again');
    }
  }

  async function editPrice(item, price) {
    const p = Number(price);
    if (isNaN(p) || price === '') { showToast('Enter a price like 18.50'); return false; }
    if (p < 0) { showToast('Price cannot be negative'); return false; }
    if (p === Number(item.price)) return true; // no-op, don't spend a write
    const before = item.price;
    setMenu((prev) => prev.map((m) => (m.itemId === item.itemId ? { ...m, price: p } : m)));
    const res = await updateMenuItem(hotel.id, { ...item, price: p });
    if (res && res.persisted === false) {
      setMenu((prev) => prev.map((m) => (m.itemId === item.itemId ? { ...m, price: before } : m)));
      showToast('Could not save the price — check your connection');
      return false;
    }
    return true;
  }

  function openMenuEditor(item) {
    const key = item ? (item.itemId || item.id) : `item-${Date.now().toString(36)}`;
    setEditingMenuItem(item || null);
    setMenuDraft({
      itemId: key, id: key,
      name: item?.name || '',
      // Default a NEW item to whichever category the restaurant actually uses first
      // rather than hardcoding 'Entrees' — a restaurant with no Entrees section had
      // every new item land in a category that did not exist.
      category: item?.category || categories[0]?.name || 'Uncategorized',
      description: item?.description || '',
      price: item?.price != null ? String(item.price) : '',
      emoji: item?.emoji || '🍽️',
      image: item?.image || '',
      inStock: item?.inStock !== false,
    });
    setMenuEditorOpen(true);
  }

  // Upload happens immediately so the photo is already stored by the time the
  // item is saved — the draft only ever carries a URL, never image bytes.
  async function uploadPhoto(file) {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const { url } = await uploadMenuPhoto(hotel.id, menuDraft.itemId, file);
      setMenuDraft((d) => ({ ...d, image: url }));
      // showToast, not setToast: setToast skips the 2.6s dismissal timer, so the
      // upload message used to sit on screen until something else replaced it.
      showToast('Photo added — save the item to publish it');
    } catch (err) {
      showToast(err.message || 'Upload failed');
    } finally {
      setPhotoUploading(false);
    }
  }

  async function saveMenuDraft() {
    const name = (menuDraft?.name || '').trim();
    if (!name) { showToast('Give the item a name'); return; }
    const price = Number(menuDraft.price || 0);
    if (isNaN(price) || price < 0) { showToast('Enter a price like 18.50'); return; }

    const key = menuDraft.itemId;
    const payload = {
      ...menuDraft,
      name,
      category: (menuDraft.category || '').trim() || 'Uncategorized',
      price,
      inStock: menuDraft.inStock !== false,
    };
    const previous = menu;
    setMenu((prev) => {
      const exists = prev.some((m) => (m.itemId || m.id) === key);
      return exists ? prev.map((m) => ((m.itemId || m.id) === key ? { ...m, ...payload } : m)) : [...prev, payload];
    });
    setSavingItem(key);
    const res = await updateMenuItem(hotel.id, payload);
    setSavingItem(null);

    if (res && res.persisted === false) {
      setMenu(previous);
      showToast('Could not save — check your connection and try again');
      return; // leave the sheet open so the edit is not lost
    }
    showToast(editingMenuItem ? 'Item updated' : 'Item added');
    setMenuEditorOpen(false); setEditingMenuItem(null); setMenuDraft(null);
  }

  async function deleteMenuEntry(item) {
    const key = item.itemId || item.id;
    const previous = menu;
    setMenu((prev) => prev.filter((m) => (m.itemId || m.id) !== key));
    const res = await removeMenuItem(hotel.id, key);
    if (res && res.persisted === false) {
      // The delete only reached localStorage. Saying nothing here is what let items
      // "come back from the dead" on the next load.
      setMenu(previous);
      showToast('Could not delete — the item is still on the guest menu');
      return;
    }
    showToast('Item deleted');
    setMenuEditorOpen(false); setEditingMenuItem(null); setMenuDraft(null);
    setConfirmDelete(null);
  }

// ---- rule editor ----
  function openRuleEditor(rule) {
    setRuleDraft({
      ...rule,
      timeStart: rule.timeRange?.start || '22:00',
      timeEnd: rule.timeRange?.end || '06:00',
    });
    setRuleEditorOpen(true);
  }

  async function saveRuleDraft() {
    if (!ruleDraft?.name?.trim()) return;
    const { timeStart, timeEnd, ...rest } = ruleDraft;
    const rule = { ...rest, timeRange: { start: timeStart, end: timeEnd } };
    const others = (pauseRules.recurringRules || []).filter((r) => r.id !== rule.id);
    await savePause({ ...pauseRules, recurringRules: [...others, rule] });
    setRuleEditorOpen(false);
    setRuleDraft(null);
  }

  // ---- derived ----
  //
  // Memoised on `orders` and the filters rather than recomputed every render. The
  // wall clock ticks this component once a SECOND, so without this the whole order
  // list was re-filtered and re-sorted sixty times a minute on a kitchen tablet,
  // producing new array identities each time and re-rendering every card with it.
  const visible = useMemo(() => orders.filter((o) => {
    const st = o.status || 'new';
    if (['completed', 'rejected', 'refunded'].includes(st)) return false;
    if (filter !== 'all' && getType(o) !== filter) return false;
    if (query) {
      const hay = ((o.orderId || o.id || '') + ' ' + (o.roomNumber || '') + ' ' + getItems(o).map((i) => i.name).join(' ')).toLowerCase();
      if (!hay.includes(query.toLowerCase())) return false;
    }
    return true;
  }), [orders, filter, query]);

  const colNew = useMemo(() => visible.filter((o) => (o.status || 'new') === 'new'), [visible]);
  const colPrep = useMemo(() => visible.filter((o) => (o.status || 'new') === 'preparing'), [visible]);
  const colReady = useMemo(() => visible.filter((o) => (o.status || 'new') === 'ready'), [visible]);

  // What the focus view is currently showing. Oldest first everywhere — the guest
  // who has waited longest is always the one to deal with next.
  const byOldest = (a, b) => new Date(getCreated(a)) - new Date(getCreated(b));
  const stageOrders = (
    stage === 'preparing' ? colPrep : stage === 'ready' ? colReady : colNew
  ).slice().sort(byOldest);

  // all-day tally (new + preparing)
  const allDayList = useMemo(() => {
    const allDay = {};
    [...colNew, ...colPrep].forEach((o) => getItems(o).forEach((it) => { allDay[it.name] = (allDay[it.name] || 0) + it.qty; }));
    return Object.entries(allDay).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [colNew, colPrep]);

  // stats — completed today uses date in selected timezone
  const todayStr = getDateKeyInTz(Date.now(), settings.timezone);
  const completedToday = orders.filter((o) =>
    (o.status || '') === 'completed' &&
    getDateKeyInTz(o.updatedAt || o.createdAt || '', settings.timezone) === todayStr
  ).length;
  const revenueToday = orders
    .filter((o) => (o.status || '') === 'completed' && getDateKeyInTz(o.updatedAt || o.createdAt || '', settings.timezone) === todayStr)
    .reduce((sum, o) => sum + getTotal(o), 0);
  const inProgress = colNew.length + colPrep.length;

  // ---- menu derived ----
  //
  // The category list a picker offers = the managed list, plus any category names
  // items are actually using that the managed list has not caught up with. Without
  // that union, an item sitting in a legacy category would have its category
  // silently rewritten the first time someone opened and saved it.
  const categoryNames = useMemo(() => {
    const managed = categories.map((c) => c.name);
    const used = menu.map((m) => m.category).filter(Boolean);
    return [...managed, ...used.filter((n) => !managed.includes(n))]
      .filter((n, i, a) => a.indexOf(n) === i);
  }, [categories, menu]);

  // Grouped, filtered, and ordered by the MANAGED category order — the guest menu
  // reads the same order, so what the kitchen sees is what the guest sees.
  const visibleMenuGroups = useMemo(() => {
    const q = menuQuery.trim().toLowerCase();
    const filtered = menu.filter((m) => {
      if (menuStockFilter === 'in' && m.inStock === false) return false;
      if (menuStockFilter === 'out' && m.inStock !== false) return false;
      if (!q) return true;
      // Searches description and category too. The old helper matched `name` only,
      // so searching "gluten" or "Dessert" found nothing.
      return [m.name, m.description, m.category].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
    const map = new Map();
    filtered.forEach((m) => {
      const cat = m.category || 'Uncategorized';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(m);
    });
    const order = (name) => {
      const i = categoryNames.indexOf(name);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...map.entries()]
      .sort((a, b) => order(a[0]) - order(b[0]) || a[0].localeCompare(b[0]))
      .map(([cat, items]) => [cat, items.slice().sort((x, y) => x.name.localeCompare(y.name))]);
  }, [menu, menuQuery, menuStockFilter, categoryNames]);

  // ---- category management ----
  function openCategoryManager() {
    setCatDraft(categoryNames.map((name, i) => {
      const managed = categories.find((c) => c.name === name);
      return { id: managed?.id || null, name, sort: i, count: menu.filter((m) => (m.category || 'Uncategorized') === name).length };
    }));
    setCatManagerOpen(true);
  }

  async function saveCategoryDraft() {
    const names = catDraft.map((c) => c.name.trim());
    if (names.some((n) => !n)) { showToast('Every category needs a name'); return; }
    const lower = names.map((n) => n.toLowerCase());
    if (lower.some((n, i) => lower.indexOf(n) !== i)) { showToast('Two categories have the same name'); return; }

    setCatSaving(true);
    try {
      const res = await saveCategories(hotel.id, catDraft.map((c) => ({ id: c.id, name: c.name.trim() })));
      setCategories(res.categories || []);
      // Renames and deletions rewrite the `category` on affected items server-side,
      // so the local menu has to be re-read rather than patched.
      if (res.itemsTouched) {
        const items = await getMenu(hotel.id, staffRestaurant && staffRestaurant.restaurantId);
        setMenu(items);
      }
      setCatManagerOpen(false);
      showToast(res.itemsTouched ? `Categories saved · ${res.itemsTouched} item${res.itemsTouched === 1 ? '' : 's'} moved` : 'Categories saved');
    } catch (err) {
      showToast(err.message || 'Could not save the categories');
    } finally {
      setCatSaving(false);
    }
  }

  // One writer for every rule change, so the server and the screen cannot disagree.
  async function savePause(next) {
    const previous = pauseRules;
    setPauseRulesState(next);                       // optimistic
    try {
      const saved = await staffSetPauseRules(next);
      if (saved) setPauseRulesState(saved);
      showToast('Opening hours saved — guests see this now');
    } catch (err) {
      setPauseRulesState(previous);
      showToast(err.message || 'Could not save your opening hours');
    }
  }

  function signOut() {
    clearStaffSession();
    navigate('/staff-login');
  }

  useEffect(() => {
    pendingNewCount.current = colNew.length;
    if (settings.sound && colNew.length > 0) startAlarm(); else stopAlarm();
    return stopAlarm;
  }, [colNew.length, settings.sound, startAlarm, stopAlarm]);

  const sizeClass = settings.textSize === 'large' ? 'kp-lg' : settings.textSize === 'compact' ? 'kp-sm' : '';

  return (
    <div className={'kp ' + sizeClass} style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <style>{css}</style>

      {/* ── KDS Header ── */}
      <div style={{ flexShrink: 0 }}>
        <div className="kp-header">
          <div className="kp-header-left">
            {/* GapGo wordmark, matching LandingPage `.lg-logo` / `.lg-logo-go` and
                the sign-in screens. The restaurant's own name sits beside it, so a
                kitchen can see at a glance whose orders this screen is showing —
                which matters the moment there is more than one restaurant. */}
            <div className="kp-logo">
              <span className="kp-logo-name">Gap<span className="kp-logo-go">Go</span></span>
              {staffRestaurant && <span className="kp-logo-sep" />}
              {staffRestaurant && <span className="kp-logo-venue">{staffRestaurant.name}</span>}
            </div>
            <div className="kp-open-pill" data-closed={pauseState?.paused ? 'true' : 'false'}>
              <span className="kp-open-dot" />
              {pauseState?.paused ? (pauseState.reason || 'Closed') : 'Open'}
            </div>
          </div>
          <div className="kp-header-center">
            {(() => {
              const tzNow = convertToTimezone(clock, settings.timezone);
              const timeStr = tzNow.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
              const dateStr = tzNow.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
              return (
                <div className="kp-live-clock">
                  <span className="kp-clock-time">{timeStr}</span>
                  <span className="kp-clock-date">{dateStr} · {settings.timezone} · {APP_VERSION}</span>
                </div>
              );
            })()}
          </div>
          <div className="kp-header-right">
            <button className="kp-icon-btn" title="Refresh" onClick={loadOrders}>⟳</button>
            <button className="kp-profile-btn" title="Profile" onClick={() => setProfileOpen(true)}>
              <span className="kp-profile-avatar">R</span>
            </button>
          </div>
        </div>

        {/* ── Nav tabs ──
            Account is a real tab now. The access code and account screens used to be
            two floating pills docked to the bottom-left corner of the viewport,
            outside the navigation entirely — findable only by knowing they were
            there. Settings moves under Account with them. */}
        <nav className="kp-nav">
          {[
            ['orders', 'Orders', inProgress],
            ['menu', 'Menu', 0],
            ['history', 'History', 0],
            ['account', 'Account', 0],
          ].map(([id, label, badge]) => (
            <button key={id} className={'kp-nav-btn' + (tab === id ? ' on' : '')} onClick={() => setTab(id)}>
              {label}
              {badge > 0 && <span className="kp-nav-badge">{badge}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Content area (scrollable) */}
      <div style={{ flex: 1, overflow: 'auto' }}>

      {/* ORDERS TAB */}
      {tab === 'orders' && (
        <main className="kp-main">
          {/* stats + filters */}
          <div className="kp-toolbar glass">
            <div className="kp-stats">
              <div className="kp-stat"><span className="kp-stat-n">{inProgress}</span><span className="kp-stat-l">In progress</span></div>
              <div className="kp-stat"><span className="kp-stat-n">{colNew.length}</span><span className="kp-stat-l">New</span></div>
              <div className="kp-stat"><span className="kp-stat-n">{completedToday}</span><span className="kp-stat-l">Done today</span></div>
            </div>
            <div className="kp-toolbar-right">
              <div className="kp-search">
                <Search />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search order or room" />
              </div>
              <div className="kp-filters">
                {[['all', 'All'], ['room', 'Room'], ['table', 'Table'], ['pickup', 'Pickup'], ['delivery', 'Delivery']].map(([id, label]) => (
                  <button key={id} className={'kp-chip' + (filter === id ? ' on' : '')} onClick={() => setFilter(id)}>{label}</button>
                ))}
              </div>
              <button className="kp-recall" onClick={recallLast}>Recall</button>
            </div>
          </div>

          {/* all-day strip */}
          {allDayList.length > 0 && (
            <div className="kp-allday glass">
              <span className="kp-allday-label">All day</span>
              {allDayList.map(([name, qty]) => (
                <span key={name} className="kp-allday-item"><b>{qty}</b> {name}</span>
              ))}
            </div>
          )}

          {/* FOCUS VIEW — one stage at a time.
              Three columns on a tablet means three cramped lists and small targets,
              in a kitchen, at speed. Segmented tabs carry the counts so nothing is
              hidden, and the active list gets the whole width for big rows.
              `classic` keeps the old three-column view one toggle away — cheap
              insurance on the screen Darcy's depends on every service. */}
          {loading ? (
            <div className="kp-empty">Loading orders...</div>
          ) : settings.kitchenView !== 'classic' ? (
            <>
              <div className="kp-stage-tabs">
                {[
                  ['new', 'New', colNew.length],
                  ['preparing', 'Preparing', colPrep.length],
                  ['ready', 'Ready', colReady.length],
                ].map(([id, label, n]) => (
                  <button
                    key={id}
                    className={'kp-stage-tab' + (stage === id ? ' on' : '')}
                    data-stage={id}
                    onClick={() => setStage(id)}
                  >
                    <span className="kp-stage-label">{label}</span>
                    <span className="kp-stage-count">{n}</span>
                  </button>
                ))}
              </div>

              <div className="kp-stage-list">
                {stageOrders.length === 0 && (
                  <Empty text={
                    stage === 'new' ? 'No new orders'
                      : stage === 'preparing' ? 'Nothing cooking'
                        : 'Nothing waiting'
                  } />
                )}
                {stageOrders.map((o) => (
                  <OrderCard
                    key={o.orderId || o.id} order={o} now={now} lateAfter={settings.lateAfter}
                    timezone={settings.timezone}
                    onOpen={() => setDetail(o)}
                    primary={
                      stage === 'new' ? { label: 'Accept', prepChoice: true, onClick: (mins) => accept(o, mins) }
                        : stage === 'preparing' ? { label: 'Mark ready', onClick: () => markReady(o) }
                          : { label: 'Complete', onClick: () => complete(o) }
                    }
                    secondary={
                      stage === 'new' ? [{ label: 'Reject', onClick: () => setRejecting(o), danger: true }]
                        : stage === 'ready' ? [{ label: 'Back', onClick: () => bumpBack(o) }]
                          : []
                    }
                    on86={eightySix} />
                ))}
              </div>
            </>
          ) : (
            <div className="kp-cols">
              <Column title="New" tone="new" count={colNew.length}>
                {colNew.length === 0 && <Empty text="No new orders" />}
                {colNew.map((o) => (
                  <OrderCard key={o.orderId || o.id} order={o} now={now} lateAfter={settings.lateAfter}
                    timezone={settings.timezone}
                    primary={{ label: 'Accept', prepChoice: true, onClick: (mins) => accept(o, mins) }}
                    secondary={[{ label: 'Reject', onClick: () => setRejecting(o), danger: true }]}
                    on86={eightySix} />
                ))}
              </Column>

              <Column title="Preparing" tone="prep" count={colPrep.length}>
                {colPrep.length === 0 && <Empty text="Nothing cooking" />}
                {colPrep.map((o) => (
                  <OrderCard key={o.orderId || o.id} order={o} now={now} lateAfter={settings.lateAfter}
                    timezone={settings.timezone}
                    primary={{ label: 'Mark Ready', onClick: () => markReady(o) }}
                    secondary={[]}
                    on86={eightySix} />
                ))}
              </Column>

              <Column title="Ready" tone="ready" count={colReady.length}>
                {colReady.length === 0 && <Empty text="Nothing waiting" />}
                {colReady.map((o) => (
                  <OrderCard key={o.orderId || o.id} order={o} now={now} lateAfter={settings.lateAfter}
                    timezone={settings.timezone}
                    primary={{ label: 'Complete', onClick: () => complete(o) }}
                    secondary={[{ label: 'Back', onClick: () => bumpBack(o) }]}
                    on86={eightySix} />
                ))}
              </Column>
            </div>
          )}
        </main>
      )}

      {/* HISTORY TAB */}
      {tab === 'history' && (
        <main className="kp-main">
          {/* stats + filters */}
          <div className="kp-toolbar glass" style={{ flexWrap: 'wrap' }}>
            <div className="kp-stats">
              <div className="kp-stat"><span className="kp-stat-n">{completedToday}</span><span className="kp-stat-l">Completed today</span></div>
              <div className="kp-stat"><span className="kp-stat-n">A${revenueToday.toFixed(0)}</span><span className="kp-stat-l">Revenue today</span></div>
            </div>
            <div className="kp-toolbar-right" style={{ flexWrap: 'wrap' }}>
              <div className="kp-filters">
                {[['today', 'Today'], ['7days', '7 days'], ['all', 'All time']].map(([id, label]) => (
                  <button key={id} className={'kp-chip' + (histDate === id ? ' on' : '')} onClick={() => setHistDate(id)}>{label}</button>
                ))}
              </div>
              <div className="kp-filters">
                {[['all', 'All'], ['completed', 'Completed'], ['rejected', 'Rejected'], ['refunded', 'Refunded']].map(([id, label]) => (
                  <button key={id} className={'kp-chip' + (histStatus === id ? ' on' : '')} onClick={() => setHistStatus(id)}>{label}</button>
                ))}
              </div>
              <select className="kp-hotel" value={histHotel} onChange={(e) => setHistHotel(e.target.value)}>
                <option value="all">All Hotels</option>
                {HOTELS.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
              <div className="kp-search">
                <Search />
                <input value={histQuery} onChange={(e) => setHistQuery(e.target.value)} placeholder="Search order or item" />
              </div>
            </div>
          </div>

          {/* grouped order log */}
          {(() => {
            const filtered = orders
              .filter((o) => ['completed', 'rejected', 'refunded'].includes(o.status || ''))
              .filter((o) => {
                const ts = getCreated(o) || o.updatedAt || '';
                if (histDate === 'today') return getDateKeyInTz(ts, settings.timezone) === todayStr;
                if (histDate === '7days') return Date.now() - new Date(ts || 0).getTime() < 7 * 24 * 3600000;
                return true;
              })
              .filter((o) => histStatus === 'all' || o.status === histStatus)
              .filter((o) => histHotel === 'all' || o.hotelId === histHotel)
              .filter((o) => {
                if (!histQuery) return true;
                const hay = ((o.orderId || o.id || '') + ' ' + (o.roomNumber || '') + ' ' + getItems(o).map((i) => i.name).join(' ')).toLowerCase();
                return hay.includes(histQuery.toLowerCase());
              })
              .sort((a, b) => new Date(getCreated(b) || 0) - new Date(getCreated(a) || 0));

            if (!filtered.length) return <div className="kp-empty">No orders match your filters</div>;

            const yKey = getDateKeyInTz(Date.now() - 86400000, settings.timezone);
            const groups = [];
            const seen = {};
            filtered.forEach((o) => {
              const ts = getCreated(o) || o.updatedAt || '';
              const key = ts ? getDateKeyInTz(ts, settings.timezone) : 'unknown';
              const label = key === todayStr ? 'Today' : key === yKey ? 'Yesterday'
                : ts ? new Date(ts).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Unknown';
              if (!seen[key]) { seen[key] = true; groups.push({ label, key, items: [] }); }
              groups[groups.length - 1].items.push(o);
            });

            return groups.map((g) => (
              <div key={g.key}>
                <div className="kp-hist-group-label">{g.label} · {g.items.length} order{g.items.length !== 1 ? 's' : ''}</div>
                {g.items.map((o) => <HistoryRow key={o.orderId || o.id} order={o} timezone={settings.timezone} />)}
              </div>
            ));
          })()}
        </main>
      )}

      {/* MENU TAB */}
      {tab === 'menu' && (
        <main className="kp-main">
          <div className="kp-toolbar glass">
            <div className="kp-menu-head">
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <h2 className="kp-menu-title">Menu</h2>
                <select className="kp-hotel" value={hotel.id} onChange={(e) => setHotel(HOTELS.find((h) => h.id === e.target.value))}>
                  {HOTELS.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              </div>
              <p className="kp-menu-sub">
                {menu.length} item{menu.length === 1 ? '' : 's'} in {categoryNames.length} categor{categoryNames.length === 1 ? 'y' : 'ies'}
                {' · '}out-of-stock items are hidden from guests
              </p>
            </div>
            <div className="kp-toolbar-right">
              <div className="kp-filters">
                {[['all', 'All'], ['in', 'In stock'], ['out', 'Out of stock']].map(([id, label]) => (
                  <button key={id} className={'kp-chip' + (menuStockFilter === id ? ' on' : '')} onClick={() => setMenuStockFilter(id)}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="kp-search kp-search-wide">
                <Search />
                <input value={menuQuery} onChange={(e) => setMenuQuery(e.target.value)} placeholder="Search items" />
              </div>
              <button className="kp-recall" onClick={openCategoryManager}>Categories</button>
              <button className="kp-act-primary" style={{ flex:'none', padding:'9px 18px' }} onClick={() => openMenuEditor()}>+ Add item</button>
            </div>
          </div>

          {!menuLoaded ? (
            <div className="kp-empty">Loading menu...</div>
          ) : visibleMenuGroups.length === 0 ? (
            <div className="kp-empty">
              {menuQuery ? `Nothing matches "${menuQuery}".` : 'No items yet — add your first one.'}
            </div>
          ) : (
            <div className="kp-menu-list">
              {visibleMenuGroups.map(([category, items]) => (
                <div key={category} className="kp-menu-group">
                  <div className="kp-menu-cat">
                    {category}
                    <span className="kp-menu-cat-count">
                      {items.length} item{items.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {items.map((item) => (
                    /* The row is NOT a click target any more.
                       It used to be `onClick={() => openMenuEditor(item)}` wrapping
                       the price field and the availability switch, neither of which
                       stopped propagation — so tapping the toggle also opened the
                       edit sheet on top of it, and the price input could never be
                       focused. That single bug is why the menu screen was unusable
                       and why "we can't add or change items" was the report.
                       Editing is now an explicit button. */
                    <div key={item.itemId} className={'kp-menu-row' + (item.inStock ? '' : ' off')}>
                      {item.image
                        ? <img className="kp-menu-thumb" src={item.image} alt="" loading="lazy" />
                        : <span className="kp-menu-thumb-ph">{item.emoji || '\u{1F37D}'}</span>}
                      <div className="kp-menu-info">
                        <span className="kp-menu-name">{item.name}</span>
                        {item.description && <span className="kp-menu-desc">{item.description}</span>}
                        <span className={'kp-menu-stock ' + (item.inStock ? 'in' : 'out')}>
                          {item.inStock ? 'In stock' : 'Out of stock'}
                        </span>
                      </div>
                      <div className="kp-menu-price">
                        <span className="kp-menu-cur">A$</span>
                        <input
                          className="kp-menu-price-input"
                          type="number"
                          step="0.01"
                          min="0"
                          /* Keyed on the item's own price so it re-syncs when the
                             menu changes underneath — as an uncontrolled input with
                             a bare defaultValue it kept showing a stale figure after
                             a failed save. */
                          key={item.itemId + ':' + item.price}
                          defaultValue={item.price}
                          onBlur={(e) => editPrice(item, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                      </div>
                      <button className="kp-menu-edit" onClick={() => openMenuEditor(item)}>Edit</button>
                      <button
                        className={'kp-switch' + (item.inStock ? ' on' : '')}
                        onClick={() => toggleStock(item)}
                        title={item.inStock ? 'Mark out of stock' : 'Mark in stock'}
                        aria-label="Toggle availability">
                        <span className="kp-switch-knob" />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </main>
      )}

      {/* ACCOUNT TAB */}
      {tab === 'account' && (
        <main className="kp-main">
          <AccountTab
            section={acctSection}
            onSection={setAcctSection}
            restaurant={staffRestaurant}
            settings={settings}
            onSettings={setSettings}
            onToast={showToast}
            onSignOut={signOut}
            onOpenSettingsSheet={() => setSettingsOpen(true)}
          />
        </main>
      )}

      {/* SETTINGS SHEET */}
      {settingsOpen && (
        <div className="kp-sheet-wrap" onClick={() => setSettingsOpen(false)}>
          <div className="kp-sheet glass-strong" onClick={(e) => e.stopPropagation()}>
            <div className="kp-sheet-grab" />
            <div className="kp-sheet-head">
              <h3>Settings</h3>
              <button className="kp-sheet-close" onClick={() => setSettingsOpen(false)}>Done</button>
            </div>

            <div className="kp-sheet-body">
              <Group label="Sound">
                <RowButton label="Test sound" onClick={playAlert} />
                <RowSlider label="Volume" value={settings.volume} min={0} max={1} step={0.05}
                  onChange={(v) => setSettings((s) => ({ ...s, volume: v }))} display={Math.round(settings.volume * 100) + '%'} />
              </Group>

              <Group label="Restaurant Status">
                <RowToggle label="Pause mode (closed)" value={pauseMode.active} onChange={(v) => {
                  const next = { ...pauseMode, active: v };
                  setPauseMode(next);
                  localStorage.setItem('gapgo_pause_mode_' + hotel.id, JSON.stringify(next));
                }} />
                {pauseMode.active && (
                  <div style={{ paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '12px' }}>
                    <p style={{ fontSize: '12px', color: '#888', margin: '0 0 10px' }}>Available categories when closed:</p>
                    {['Beverages', 'Snacks', 'Desserts', 'Sides'].map((cat) => (
                      <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', cursor: 'pointer' }}
                        onClick={() => {
                          const next = {
                            ...pauseMode,
                            availableCategories: pauseMode.availableCategories.includes(cat)
                              ? pauseMode.availableCategories.filter((c) => c !== cat)
                              : [...pauseMode.availableCategories, cat]
                          };
                          setPauseMode(next);
                          localStorage.setItem('gapgo_pause_mode_' + hotel.id, JSON.stringify(next));
                        }}>
                        <input type="checkbox" checked={pauseMode.availableCategories.includes(cat)} readOnly style={{ cursor: 'pointer' }} />
                        <span style={{ fontSize: '14px', color: '#ccc' }}>{cat}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Group>

              <Group label="Pause Rules (Scheduling)">
                <RowToggle label="Enable schedule rules" value={(pauseRules.recurringRules || []).length > 0} onChange={(v) => {
                  if (v && (pauseRules.recurringRules || []).length === 0) {
                    savePause({
                      ...pauseRules,
                      recurringRules: [{
                        id: 'rule-' + Date.now(),
                        name: 'After hours',
                        active: true,
                        daysOfWeek: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
                        timeRange: { start: '22:00', end: '06:00' },
                        // Seeded from a category this restaurant actually has —
                        // 'Beverages' was hardcoded and Darcy's has no such section,
                        // so the example rule closed the kitchen completely.
                        availableCategories: categoryNames.slice(0, 1),
                        priority: 10,
                      }],
                    });
                  } else if (!v && (pauseRules.recurringRules || []).length) {
                    // Turning the toggle OFF used to do nothing at all.
                    savePause({ ...pauseRules, recurringRules: [] });
                  }
                }} />
                <div style={{ fontSize: '12px', color: 'var(--txt3)', marginTop: '8px', padding: '10px 12px', background: 'var(--sunken)', border: '1px solid var(--brd)', borderRadius: '10px', lineHeight: 1.5 }}>
                  Rules apply automatically by time and day, and <strong style={{ color: 'var(--txt2)' }}>guests see them</strong> — outside these hours only the listed categories can be ordered. A manual pause overrides every rule.
                </div>
                {(pauseRules.recurringRules || []).map((rule) => (
                  <div key={rule.id} style={{ marginTop: '12px', padding: '12px', background: 'var(--sunken)', border: '1px solid var(--brd)', borderRadius: '10px', fontSize: '12px' }}>
                    <div style={{ fontWeight: 700, color: 'var(--txt)', marginBottom: '6px' }}>{rule.name}</div>
                    <div style={{ color: 'var(--txt2)', marginBottom: '4px' }}>
                      {rule.daysOfWeek?.join(', ')}, {rule.timeRange?.start}–{rule.timeRange?.end}
                    </div>
                    <div style={{ color: 'var(--txt3)', marginBottom: '8px' }}>
                      Available: {rule.availableCategories?.join(', ') || 'None (fully closed)'}
                    </div>
<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start' }}>
                       <button onClick={() => {
                         openRuleEditor(rule);
                       }} className="kp-menu-edit" style={{ padding: '4px 10px' }}>
                         Edit
                       </button>
                       <button onClick={() => {
                         savePause({ ...pauseRules, recurringRules: (pauseRules.recurringRules || []).filter((r) => r.id !== rule.id) });
                       }} className="kp-act-sec danger" style={{ minHeight: 0, padding: '4px 10px', fontSize: '11px' }}>
                         Delete
                       </button>
                     </div>
                  </div>
                ))}
              </Group>

              <Group label="Timezone">
                <select
                  value={settings.timezone}
                  onChange={(e) => setSettings((s) => ({ ...s, timezone: e.target.value }))}
                  style={{
                    width: '100%', padding: '10px', background: '#27272a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit'
                  }}>
                  {TIMEZONES.map((tz) => <option key={tz.id} value={tz.id}>{tz.name}</option>)}
                </select>
              </Group>

              <Group label="Updates">
                <RowToggle label="Auto refresh" value={settings.autoRefresh} onChange={(v) => setSettings((s) => ({ ...s, autoRefresh: v }))} />
                <RowStepper label="Refresh every" value={settings.refreshSecs} suffix="s" min={5} max={120} step={5} disabled={!settings.autoRefresh}
                  onChange={(v) => setSettings((s) => ({ ...s, refreshSecs: v }))} />
                <RowStepper label="Flag late after" value={settings.lateAfter} suffix="m" min={5} max={45} step={5}
                  onChange={(v) => setSettings((s) => ({ ...s, lateAfter: v }))} />
              </Group>

              <Group label="Display">
                <RowSegmented label="Order layout" value={settings.kitchenView || 'focus'}
                  options={[['focus', 'Focus'], ['classic', 'Columns']]}
                  onChange={(v) => setSettings((s) => ({ ...s, kitchenView: v }))} />
                <RowSegmented label="Text size" value={settings.textSize}
                  options={[['compact', 'Compact'], ['normal', 'Normal'], ['large', 'Large']]}
onChange={(v) => setSettings((s) => ({ ...s, textSize: v }))} />
              </Group>

              <Group label="Maintenance">
                <RowButton label="Refresh orders now" onClick={() => { loadOrders(); setSettingsOpen(false); }} />
              </Group>
            </div>
          </div>
        </div>
      )}

      {/* REJECT SHEET */}
      {/* NEW ORDER — takes over the screen.
          Deliberately NOT dismissable by tapping the backdrop: the whole point is
          that it cannot be walked past. The only ways out are accepting with a
          prep time, rejecting with a reason, or "Later", which hands it back to
          the New column rather than pretending it was dealt with. */}
      {incoming && !rejecting && (
        <div className="kp-incoming-wrap">
          <div className="kp-incoming">
            <div className="kp-incoming-head">
              <span className="kp-incoming-badge">New order</span>
              <span className="kp-incoming-ago">{timeLabel(getCreated(incoming))}</span>
            </div>

            <div className="kp-incoming-where">
              {incoming.roomNumber && <span className="kp-incoming-room">Room {incoming.roomNumber}</span>}
              <span className="kp-incoming-hotel">{getHotelLabel(incoming.hotelId)}</span>
            </div>

            <div className="kp-incoming-items">
              {getItems(incoming).map((it, i) => (
                <div className="kp-incoming-line" key={i}>
                  <span className="kp-incoming-qty">{it.qty}×</span>
                  <span className="kp-incoming-name">{it.name}</span>
                </div>
              ))}
            </div>

            {incoming.notes && <div className="kp-incoming-note">“{incoming.notes}”</div>}

            <div className="kp-incoming-total">
              <span>Total</span>
              <b>${Number(getTotal(incoming)).toFixed(2)}</b>
            </div>

            <div className="kp-prep-label">Accept — ready in</div>
            <div className="kp-prep-row">
              {PREP_OPTIONS.map((m) => (
                <button key={m} className="kp-prep-chip" onClick={() => accept(incoming, m)}>
                  {m}<span>min</span>
                </button>
              ))}
            </div>

            <div className="kp-incoming-foot">
              <button className="kp-act-sec" onClick={() => setIncoming(null)}>Later</button>
              <button className="kp-act-sec danger" onClick={() => setRejecting(incoming)}>Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* ORDER DETAIL — everything about one order, without leaving the list. */}
      {detail && (
        <div className="kp-sheet-wrap" onClick={() => setDetail(null)}>
          <div className="kp-sheet glass-strong kp-sheet-sm" onClick={(e) => e.stopPropagation()}>
            <div className="kp-sheet-grab" />
            <div className="kp-sheet-head">
              <h3>Room {detail.roomNumber || '—'}</h3>
              <button className="kp-sheet-close" onClick={() => setDetail(null)}>Close</button>
            </div>
            <div className="kp-sheet-body">
              <div className="kp-detail-meta">
                <div><span>Order</span><b>#{(detail.orderId || '').slice(-6).toUpperCase()}</b></div>
                <div><span>Placed</span><b>{formatTimeInTz(getCreated(detail), settings.timezone || 'AEST')}</b></div>
                {detail.prepMins && <div><span>Promised</span><b>{detail.prepMins} min</b></div>}
                {detail.readyBy && <div><span>Ready by</span><b>{formatTimeInTz(detail.readyBy, settings.timezone || 'AEST')}</b></div>}
                <div><span>Guest</span><b>{detail.guestName || '—'}</b></div>
                {detail.guestPhone && <div><span>Phone</span><b>{detail.guestPhone}</b></div>}
                <div><span>Payment</span><b>{(detail.paymentStatus || 'unknown')}</b></div>
              </div>

              <div className="kp-detail-items">
                {getItems(detail).map((it, i) => (
                  <div className="kp-incoming-line" key={i}>
                    <span className="kp-incoming-qty">{it.qty}×</span>
                    <span className="kp-incoming-name">{it.name}</span>
                  </div>
                ))}
              </div>

              {detail.notes && <div className="kp-incoming-note">“{detail.notes}”</div>}

              <div className="kp-incoming-total">
                <span>Total</span>
                <b>${Number(getTotal(detail)).toFixed(2)}</b>
              </div>
            </div>
          </div>
        </div>
      )}

      {rejecting && (
        <div className="kp-sheet-wrap" onClick={() => setRejecting(null)}>
          <div className="kp-sheet glass-strong kp-sheet-sm" onClick={(e) => e.stopPropagation()}>
            <div className="kp-sheet-grab" />
            <div className="kp-sheet-head">
              <h3>Reject order</h3>
              <button className="kp-sheet-close" onClick={() => setRejecting(null)}>Cancel</button>
            </div>
            <div className="kp-sheet-body">
              <p className="kp-reject-sub">Pick a reason. The guest will be emailed.</p>
              {REJECT_REASONS.map((r) => (
                <button key={r.code} className="kp-reason" onClick={() => confirmReject(rejecting, r)}>{r.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* RULE EDITOR SHEET */}
      {ruleEditorOpen && ruleDraft && (
        <div className="kp-sheet-wrap" onClick={() => { setRuleEditorOpen(false); setRuleDraft(null); }}>
          <div className="kp-sheet glass-strong kp-sheet-sm" onClick={(e) => e.stopPropagation()}>
            <div className="kp-sheet-grab" />
            <div className="kp-sheet-head">
              <h3>Edit Pause Rule</h3>
              <button className="kp-sheet-close" onClick={() => { setRuleEditorOpen(false); setRuleDraft(null); }}>Cancel</button>
            </div>
            <div className="kp-sheet-body">
              <div className="kp-menu-form">
                <label className="kp-form-field">
                  <span>Rule name</span>
                  <input type="text" placeholder="e.g. Night Close" value={ruleDraft.name || ''} onChange={(e) => setRuleDraft((d) => ({ ...d, name: e.target.value }))} />
                </label>
                <label className="kp-form-field">
                  <span>Start time</span>
                  <input type="time" value={ruleDraft.timeStart || '22:00'} onChange={(e) => setRuleDraft((d) => ({ ...d, timeStart: e.target.value }))} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px 12px', color: '#fff', fontSize: '14px', fontFamily: 'inherit' }} />
                </label>
                <label className="kp-form-field">
                  <span>End time</span>
                  <input type="time" value={ruleDraft.timeEnd || '06:00'} onChange={(e) => setRuleDraft((d) => ({ ...d, timeEnd: e.target.value }))} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px 12px', color: '#fff', fontSize: '14px', fontFamily: 'inherit' }} />
                </label>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '10px', marginBottom: '8px' }}>Days</div>
                {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((day) => (
                  <div key={day} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 2px', cursor: 'pointer' }}
                    onClick={() => {
                      const days = ruleDraft.daysOfWeek || [];
                      const newDays = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
                      setRuleDraft((d) => ({ ...d, daysOfWeek: newDays }));
                    }}>
                    <input type="checkbox" checked={ruleDraft.daysOfWeek?.includes(day) || false} readOnly style={{ cursor: 'pointer' }} />
                    <span style={{ fontSize: '13px', color: '#ccc' }}>{day}</span>
                  </div>
                ))}
                <div style={{ fontSize: '12px', color: '#888', marginTop: '12px', marginBottom: '8px' }}>Available categories</div>
                {['Beverages', 'Snacks', 'Desserts', 'Sides'].map((cat) => (
                  <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 2px', cursor: 'pointer' }}
                    onClick={() => {
                      const cats = ruleDraft.availableCategories || [];
                      const newCats = cats.includes(cat) ? cats.filter((c) => c !== cat) : [...cats, cat];
                      setRuleDraft((d) => ({ ...d, availableCategories: newCats }));
                    }}>
                    <input type="checkbox" checked={ruleDraft.availableCategories?.includes(cat) || false} readOnly style={{ cursor: 'pointer' }} />
                    <span style={{ fontSize: '13px', color: '#ccc' }}>{cat}</span>
                  </div>
                ))}
              </div>
              <div className="kp-form-actions">
                <button className="kp-act-sec" onClick={() => { setRuleEditorOpen(false); setRuleDraft(null); }} type="button">Cancel</button>
                <button className="kp-act-primary" onClick={saveRuleDraft} type="button" style={{ flex: 1 }}>
                  Save rule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MENU EDITOR SHEET */}
      {menuEditorOpen && menuDraft && (
        <div className="kp-sheet-wrap" onClick={() => { setMenuEditorOpen(false); setEditingMenuItem(null); setMenuDraft(null); }}>
          <div className="kp-sheet glass-strong kp-sheet-sm" onClick={(e) => e.stopPropagation()}>
            <div className="kp-sheet-grab" />
            <div className="kp-sheet-head">
              <h3>{editingMenuItem ? 'Edit item' : 'Add item'}</h3>
              <button className="kp-sheet-close" onClick={() => { setMenuEditorOpen(false); setEditingMenuItem(null); setMenuDraft(null); }}>Cancel</button>
            </div>
            <div className="kp-sheet-body">
              <div className="kp-menu-form">
                <label className="kp-form-field">
                  <span>Name</span>
                  <input type="text" placeholder="e.g. Wagyu Burger" value={menuDraft.name}
                    onChange={(e) => setMenuDraft((d) => ({ ...d, name: e.target.value }))} />
                </label>

                {/* Category is a PICKER, not a free-text box.
                    As a text input, "Deserts" or a trailing space silently created a
                    whole new section on the guest menu that nobody meant to make, and
                    renaming one meant editing every item by hand. */}
                <label className="kp-form-field">
                  <span>Category</span>
                  <select
                    value={categoryNames.includes(menuDraft.category) ? menuDraft.category : '__new'}
                    onChange={(e) => {
                      if (e.target.value === '__new') { setMenuDraft((d) => ({ ...d, category: '' })); return; }
                      setMenuDraft((d) => ({ ...d, category: e.target.value }));
                    }}>
                    {categoryNames.map((name) => <option key={name} value={name}>{name}</option>)}
                    <option value="__new">+ New category…</option>
                  </select>
                  {!categoryNames.includes(menuDraft.category) && (
                    <input
                      type="text"
                      autoFocus
                      placeholder="New category name"
                      value={menuDraft.category}
                      onChange={(e) => setMenuDraft((d) => ({ ...d, category: e.target.value }))}
                    />
                  )}
                </label>

                <label className="kp-form-field">
                  <span>Price (A$)</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00" value={menuDraft.price}
                    onChange={(e) => setMenuDraft((d) => ({ ...d, price: e.target.value }))} />
                </label>
                <label className="kp-form-field">
                  <span>Emoji</span>
                  <input type="text" placeholder="🍽️" value={menuDraft.emoji}
                    onChange={(e) => setMenuDraft((d) => ({ ...d, emoji: e.target.value }))} />
                  <span className="kp-form-hint">Shown when there is no photo.</span>
                </label>
                <label className="kp-form-field">
                  <span>Description</span>
                  <textarea rows={2} placeholder="Short description" value={menuDraft.description}
                    onChange={(e) => setMenuDraft((d) => ({ ...d, description: e.target.value }))} />
                </label>
                <label className="kp-form-field">
                  <span>Photo</span>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{
                      width: 72, height: 72, borderRadius: 10, flexShrink: 0, overflow: 'hidden',
                      background: 'var(--sunken)', border: '1px solid var(--brd)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: 26,
                    }}>
                      {menuDraft.image
                        ? <img src={menuDraft.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span>{menuDraft.emoji || '🍽️'}</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        disabled={photoUploading}
                        onChange={(e) => uploadPhoto(e.target.files && e.target.files[0])}
                        style={{ fontSize: 13, maxWidth: '100%' }}
                      />
                      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                        {photoUploading
                          ? 'Uploading…'
                          : menuDraft.image
                            ? 'Replaces the stock photo on the guest menu.'
                            : 'A real photo of this dish. Resized automatically.'}
                      </div>
                      {menuDraft.image && !photoUploading && (
                        <button type="button" className="kp-act-sec"
                          style={{ marginTop: 6, padding: '4px 10px', fontSize: 12 }}
                          onClick={() => setMenuDraft((d) => ({ ...d, image: '' }))}>
                          Remove photo
                        </button>
                      )}
                    </div>
                  </div>
                </label>
                <div className="kp-row" style={{ padding: '12px 0', borderBottom: 'none' }}>
                  <span className="kp-row-label">In stock</span>
                  <button type="button" className={'kp-switch' + (menuDraft.inStock ? ' on' : '')} onClick={() => setMenuDraft((d) => ({ ...d, inStock: !d.inStock }))}>
                    <span className="kp-switch-knob" />
                  </button>
                </div>
              </div>
              {/* Delete asks first. This used to remove a menu item on one tap, on a
                  tablet, in a kitchen, with no undo. */}
              {confirmDelete ? (
                <div className="kp-form-actions" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
                  <div className="kp-form-error">
                    Delete “{confirmDelete.name}” from the menu? Guests will stop seeing it immediately.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="kp-act-sec" type="button" style={{ flex: 1 }} onClick={() => setConfirmDelete(null)}>Keep it</button>
                    <button className="kp-act-sec danger" type="button" style={{ flex: 1 }} onClick={() => deleteMenuEntry(confirmDelete)}>Delete</button>
                  </div>
                </div>
              ) : (
                <div className="kp-form-actions">
                  {editingMenuItem && (
                    <button className="kp-act-sec danger" onClick={() => setConfirmDelete(editingMenuItem)} type="button">Delete</button>
                  )}
                  <button className="kp-act-primary" onClick={saveMenuDraft} type="button" style={{ flex: 1 }}
                    disabled={photoUploading || savingItem === menuDraft.itemId}>
                    {savingItem === menuDraft.itemId ? 'Saving…' : editingMenuItem ? 'Save changes' : 'Add item'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CATEGORY MANAGER SHEET */}
      {catManagerOpen && (
        <div className="kp-sheet-wrap" onClick={() => !catSaving && setCatManagerOpen(false)}>
          <div className="kp-sheet glass-strong kp-sheet-sm" onClick={(e) => e.stopPropagation()}>
            <div className="kp-sheet-grab" />
            <div className="kp-sheet-head">
              <h3>Categories</h3>
              <button className="kp-sheet-close" disabled={catSaving} onClick={() => setCatManagerOpen(false)}>Cancel</button>
            </div>
            <div className="kp-sheet-body">
              <p className="kp-reject-sub">
                These are the sections guests see, in this order. Renaming one moves every
                item in it; deleting one moves its items to Uncategorized rather than
                hiding them.
              </p>
              <div className="kp-menu-form">
                {catDraft.map((c, i) => (
                  <div key={c.id || 'new-' + i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <button type="button" className="kp-menu-edit" disabled={i === 0} title="Move up"
                        onClick={() => setCatDraft((d) => { const n = [...d]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}>↑</button>
                      <button type="button" className="kp-menu-edit" disabled={i === catDraft.length - 1} title="Move down"
                        onClick={() => setCatDraft((d) => { const n = [...d]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; return n; })}>↓</button>
                    </div>
                    <input
                      style={{
                        flex: 1, background: 'var(--surface)', border: '1px solid var(--brd2)',
                        borderRadius: 12, padding: '12px 14px', color: 'var(--txt)', fontSize: 14, outline: 'none',
                      }}
                      value={c.name}
                      onChange={(e) => setCatDraft((d) => d.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                    />
                    <span style={{ fontSize: 12, color: 'var(--txt3)', minWidth: 52, textAlign: 'right' }}>
                      {c.count || 0} item{c.count === 1 ? '' : 's'}
                    </span>
                    <button type="button" className="kp-act-sec danger" style={{ minHeight: 0, padding: '8px 12px', fontSize: 12 }}
                      onClick={() => setCatDraft((d) => d.filter((_, j) => j !== i))}>Remove</button>
                  </div>
                ))}
                <button type="button" className="kp-act-sec" style={{ marginTop: 4 }}
                  onClick={() => setCatDraft((d) => [...d, { id: null, name: '', sort: d.length, count: 0 }])}>
                  + Add category
                </button>
              </div>
              <div className="kp-form-actions">
                <button className="kp-act-primary" type="button" style={{ flex: 1 }} disabled={catSaving} onClick={saveCategoryDraft}>
                  {catSaving ? 'Saving…' : 'Save categories'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="kp-toast">{toast}</div>}
      </div>

      {/* PROFILE SIDE PANEL */}
      <div className={'kp-profile-backdrop' + (profileOpen ? ' open' : '')} onClick={() => setProfileOpen(false)} />
      <aside className={'kp-profile-panel' + (profileOpen ? ' open' : '')}>
        {/* panel header */}
        <div className="kp-pp-header">
          <div className="kp-pp-logo">
            <span className="kp-pp-logo-dot" />
            <span>GapGo</span>
          </div>
          <button className="kp-pp-close" onClick={() => setProfileOpen(false)}>✕</button>
        </div>

        {/* restaurant section */}
        <div className="kp-pp-section">
          <div className="kp-pp-section-label">Restaurant</div>
          <div className="kp-pp-card">
            <div className="kp-pp-row">
              <span className="kp-pp-key">Restaurant ID</span>
              <span className="kp-pp-val kp-pp-id">{RESTAURANT_ID}</span>
            </div>
            <div className="kp-pp-row">
              <span className="kp-pp-key">Location</span>
              <span className="kp-pp-val">All Hotels</span>
            </div>
            <div className="kp-pp-row">
              <span className="kp-pp-key">Status</span>
              <span className="kp-pp-val">
                <span className="kp-pp-status-dot" style={{ background: pauseState?.paused ? '#fb923c' : '#4ade80' }} />
                {pauseState?.paused ? 'Closed' : 'Open'}
              </span>
            </div>
            <div className="kp-pp-row">
              <span className="kp-pp-key">Selected hotel</span>
              <span className="kp-pp-val">{hotel.name}</span>
            </div>
          </div>
        </div>

        {/* app section */}
        <div className="kp-pp-section">
          <div className="kp-pp-section-label">App</div>
          <div className="kp-pp-card">
            <div className="kp-pp-row">
              <span className="kp-pp-key">Version</span>
              <span className="kp-pp-val kp-pp-version">{APP_VERSION}</span>
            </div>
            <div className="kp-pp-row">
              <span className="kp-pp-key">Timezone</span>
              <span className="kp-pp-val">{settings.timezone}</span>
            </div>
            <div className="kp-pp-row">
              <span className="kp-pp-key">Auto refresh</span>
              <span className="kp-pp-val">{settings.autoRefresh ? `Every ${settings.refreshSecs}s` : 'Off'}</span>
            </div>
            <div className="kp-pp-row">
              <span className="kp-pp-key">Notifications</span>
              <span className="kp-pp-val">{'Notification' in window ? Notification.permission : 'unsupported'}</span>
            </div>
          </div>
        </div>

        {/* today stats */}
        <div className="kp-pp-section">
          <div className="kp-pp-section-label">Today</div>
          <div className="kp-pp-stats-grid">
            <div className="kp-pp-stat">
              <span className="kp-pp-stat-n">{completedToday}</span>
              <span className="kp-pp-stat-l">Completed</span>
            </div>
            <div className="kp-pp-stat">
              <span className="kp-pp-stat-n">A${revenueToday.toFixed(0)}</span>
              <span className="kp-pp-stat-l">Revenue</span>
            </div>
            <div className="kp-pp-stat">
              <span className="kp-pp-stat-n">{inProgress}</span>
              <span className="kp-pp-stat-l">In progress</span>
            </div>
            <div className="kp-pp-stat">
              <span className="kp-pp-stat-n">{orders.filter(o => ['completed','rejected','refunded'].includes(o.status||'')).length}</span>
              <span className="kp-pp-stat-l">Total done</span>
            </div>
          </div>
        </div>

        {/* quick actions */}
        <div className="kp-pp-section">
          <div className="kp-pp-section-label">Quick actions</div>
          <div className="kp-pp-card">
            {[
              ['⚙', 'Settings', () => { setProfileOpen(false); setSettingsOpen(true); }],
              ['↺', 'Refresh orders', () => { loadOrders(); setProfileOpen(false); }],
              ['🔊', 'Test alert sound', playAlert],
            ].map(([icon, label, action]) => (
              <button key={label} className="kp-pp-action" onClick={action}>
                <span className="kp-pp-action-icon">{icon}</span>
                <span className="kp-pp-action-label">{label}</span>
                <span className="kp-pp-action-chev">›</span>
              </button>
            ))}
          </div>
        </div>

        <div className="kp-pp-footer">GapGo · {RESTAURANT_ID} · {APP_VERSION}</div>
      </aside>

      <RestaurantFooter variant="light" />
    </div>
  );
}

/* ---- subcomponents ---- */
function Column({ title, tone, count, children }) {
  return (
    <section className={'kp-col kp-col-' + tone}>
      <div className="kp-col-head glass">
        <span className="kp-col-dot" />
        <span className="kp-col-title">{title}</span>
        <span className="kp-col-count">{count}</span>
      </div>
      <div className="kp-col-body">{children}</div>
    </section>
  );
}

function Empty({ text }) { return <div className="kp-col-empty">{text}</div>; }

const HIST_STATUS_META = {
  completed: { label: 'Completed', color: '#4ade80' },
  rejected:  { label: 'Rejected',  color: '#ef4444' },
  refunded:  { label: 'Refunded',  color: '#fb923c' },
};

function HistoryRow({ order, timezone }) {
  const items = getItems(order);
  const total = getTotal(order);
  const ts = getCreated(order) || order.updatedAt || '';
  const type = getType(order);
  const meta = TYPE_META[type];
  const id = (order.orderId || order.id || '').toString().slice(-4).toUpperCase();
  const hotelLabel = getHotelLabel(order.hotelId);
  const status = order.status || 'completed';
  const sm = HIST_STATUS_META[status] || { label: status, color: '#888' };
  const tzTime = ts ? formatTimeInTz(ts, timezone) : '--:--';

  return (
    <div className="kp-hist-row glass">
      <div className="kp-hist-left">
        <span className="kp-hist-time">{tzTime}</span>
        <span className="kp-card-hash">#{id}</span>
        {hotelLabel && <span className="kp-hotel-tag">{hotelLabel}</span>}
        <span className="kp-tag" style={{ color: meta.color, borderColor: meta.color + '55', background: meta.color + '14' }}>{meta.label}</span>
        {order.roomNumber && <span className="kp-card-room">Rm {order.roomNumber}</span>}
      </div>
      <div className="kp-hist-items">
        {items.slice(0, 3).map((it, i) => (
          <span key={i}>{it.qty}× {it.name}{i < Math.min(items.length, 3) - 1 ? ', ' : ''}</span>
        ))}
        {items.length > 3 && <span style={{ color: 'var(--txt3)' }}> +{items.length - 3} more</span>}
      </div>
      <div className="kp-hist-right">
        <span style={{ fontSize: '14px', fontWeight: 700 }}>A${total.toFixed(2)}</span>
        <span className="kp-hist-status" style={{ color: sm.color, background: sm.color + '18', border: '1px solid ' + sm.color + '44' }}>{sm.label}</span>
        {status === 'rejected' && order.rejectReason && (
          <span className="kp-hist-reason">{order.rejectReason}</span>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order, now, lateAfter, timezone, primary, secondary, on86, onOpen }) {
  const items = getItems(order);
  const total = getTotal(order);
  const created = getCreated(order);
  const mins = minutesSince(created);

  // Once a kitchen has COMMITTED a ready time, that is the number that matters —
  // "23 min since the guest ordered" is a fact nobody can act on, while "4 min
  // over" is the kitchen's own promise slipping. Orders accepted before prep times
  // existed have no `readyBy`, so they keep the original count-up behaviour.
  const countdown = order.readyBy ? countdownLabel(order.readyBy) : null;
  const overBy = order.readyBy ? -(minutesUntil(order.readyBy) || 0) : null;
  const late = countdown !== null ? overBy > 0 : mins >= lateAfter;
  const warn = countdown !== null
    ? (overBy > -5 && overBy <= 0)
    : (mins >= Math.round(lateAfter * 0.6) && !late);
  const type = getType(order);
  const meta = TYPE_META[type];
  const id = (order.orderId || order.id || '').toString().slice(-4).toUpperCase();
  const paid = (order.paymentStatus || '').toLowerCase() === 'paid' || order.paymentIntentId;
  const priority = order.priority || order.kitchenPriority || 'MEDIUM';
  const priMeta = PRIORITY_META[priority] || PRIORITY_META.MEDIUM;
  const hotelLabel = getHotelLabel(order.hotelId);
  
  // Format time in selected timezone
  const tzTime = timezone ? formatTimeInTz(created, timezone) : formatTimeInTz(created, 'AEST');

  return (
    <article className={'kp-card glass' + (late ? ' kp-card-late' : warn ? ' kp-card-warn' : '') + (priority === 'VIP' ? ' kp-card-vip' : '')}>
      <div
        className={'kp-card-top' + (onOpen ? ' kp-card-open' : '')}
        onClick={onOpen}
        role={onOpen ? 'button' : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onKeyDown={onOpen ? (e) => { if (e.key === 'Enter') onOpen(); } : undefined}
      >
        <div className="kp-card-id">
          <span className="kp-card-hash">#{id || '----'}</span>
          <span className="kp-tag" style={{ color: meta.color, borderColor: meta.color + '55', background: meta.color + '14' }}>{meta.label}</span>
          {priMeta.label && <span className={'kp-pri ' + priMeta.cls}>{priMeta.label}</span>}
          {order.roomNumber && <span className="kp-card-room">Rm {order.roomNumber}</span>}
          {hotelLabel && <span className="kp-hotel-tag">{hotelLabel}</span>}
        </div>
        <div
          className="kp-timer-group"
          title={countdown ? `Ordered ${tzTime} · promised ${order.prepMins} min` : `Ordered at ${tzTime}`}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}
        >
          <div className={'kp-timer' + (late ? ' late' : warn ? ' warn' : '')}>
            {countdown || timeLabel(created)}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--txt3)', fontWeight: '500' }}>
            {countdown ? `${order.prepMins} min promised` : tzTime}
          </div>
        </div>
      </div>

      <div className="kp-card-items">
        {items.map((it, i) => (
          <div key={i} className="kp-line">
            <span className="kp-line-qty">{it.qty}x</span>
            <div className="kp-line-main">
              <span className="kp-line-name">{it.name}</span>
              {it.notes && <span className="kp-line-note">{it.notes}</span>}
            </div>
            <button className="kp-86" title="Mark out of stock" onClick={() => on86(it.name)}>86</button>
          </div>
        ))}
      </div>

      {order.notes && <div className="kp-card-allergy">Note: {order.notes}</div>}

      <div className="kp-card-foot">
        <span className="kp-card-total">A${total.toFixed(2)}</span>
        <span className={'kp-paid' + (paid ? ' yes' : ' no')}>{paid ? 'Paid' : 'Unpaid'}</span>
      </div>

      {/* `prepChoice` turns the single primary button into "how long will this
          take?" — the commitment that makes the countdown above mean anything.
          Only the New column passes it; everywhere else keeps one clear action. */}
      {primary.prepChoice ? (
        <>
          <div className="kp-prep-label">Accept — ready in</div>
          <div className="kp-prep-row">
            {PREP_OPTIONS.map((m) => (
              <button key={m} className="kp-prep-chip" onClick={() => primary.onClick(m)}>
                {m}<span>min</span>
              </button>
            ))}
          </div>
          <div className="kp-card-actions">
            {secondary && secondary.map((b, i) => (
              <button key={i} className={'kp-act-sec' + (b.danger ? ' danger' : '')} onClick={b.onClick}>{b.label}</button>
            ))}
          </div>
        </>
      ) : (
        <div className="kp-card-actions">
          {secondary && secondary.map((b, i) => (
            <button key={i} className={'kp-act-sec' + (b.danger ? ' danger' : '')} onClick={b.onClick}>{b.label}</button>
          ))}
          <button className="kp-act-primary" onClick={primary.onClick}>{primary.label}</button>
        </div>
      )}
    </article>
  );
}

/* settings rows */
function Group({ label, children }) {
  return (
    <div className="kp-group">
      <div className="kp-group-label">{label}</div>
      <div className="kp-group-card glass">{children}</div>
    </div>
  );
}
function RowToggle({ label, value, onChange }) {
  return (
    <div className="kp-row">
      <span className="kp-row-label">{label}</span>
      <button className={'kp-switch' + (value ? ' on' : '')} onClick={() => onChange(!value)}><span className="kp-switch-knob" /></button>
    </div>
  );
}
function RowSlider({ label, value, min, max, step, onChange, display, disabled }) {
  return (
    <div className={'kp-row' + (disabled ? ' dim' : '')}>
      <span className="kp-row-label">{label}</span>
      <div className="kp-row-right">
        <input type="range" className="kp-range" min={min} max={max} step={step} value={value} disabled={disabled}
          onChange={(e) => onChange(parseFloat(e.target.value))} />
        <span className="kp-row-val">{display}</span>
      </div>
    </div>
  );
}
function RowStepper({ label, value, onChange, min, max, step, suffix, disabled }) {
  return (
    <div className={'kp-row' + (disabled ? ' dim' : '')}>
      <span className="kp-row-label">{label}</span>
      <div className="kp-stepper">
        <button onClick={() => onChange(Math.max(min, value - step))} disabled={disabled}>-</button>
        <span className="kp-stepper-val">{value}{suffix}</span>
        <button onClick={() => onChange(Math.min(max, value + step))} disabled={disabled}>+</button>
      </div>
    </div>
  );
}
function RowButton({ label, onClick }) {
  return (
    <button className="kp-row kp-row-btn" onClick={onClick}>
      <span className="kp-row-label">{label}</span>
      <span className="kp-row-chev">{'\u203A'}</span>
    </button>
  );
}
function RowSegmented({ label, value, options, onChange }) {
  return (
    <div className="kp-row kp-row-col">
      <span className="kp-row-label">{label}</span>
      <div className="kp-seg">
        {options.map(([id, lbl]) => (
          <button key={id} className={'kp-seg-opt' + (value === id ? ' on' : '')} onClick={() => onChange(id)}>{lbl}</button>
        ))}
      </div>
    </div>
  );
}

/* icons */
const Search = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>);

// Styling lives in ./kitchenPanelCss.js — see the note at the top of that file.


export default KitchenPanel;
